import React, { useState, useEffect } from 'react';
import { klineDataService } from '../../services/klineDataService';
import { useToastStore } from '../../stores/useToastStore';
import { BarInterval, ORDER_SIDE } from '@/services/enums';

interface EditMonitorRuleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  bar: BarInterval;
  monitorData: any;
  onSuccess?: () => void;
}

const EditMonitorRuleDialog: React.FC<EditMonitorRuleDialogProps> = ({
  isOpen,
  onClose,
  symbol,
  bar,
  monitorData,
  onSuccess,
}) => {
  const showToast = useToastStore((state) => state.show);

  const [editPrompt, setEditPrompt] = useState<string>('');
  const [editData, setEditData] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      let prompt = monitorData?.aiResponse?.prompt || '';
      let analysisReport = monitorData?.aiResponse?.analysisReport || '';
      let monitorRules = monitorData?.aiResponse?.monitorRules;

      // 如果是正在运行的任务，尝试从 hash 获取 prompt
      const pHash = monitorData?.promptHash || monitorData?.aiResponse?.promptHash;
      if (!prompt && pHash) {
        const p = await klineDataService.getPrompt(pHash);
        if (p.systemPrompt) {
          prompt = `${p.systemPrompt}\n\n---\n\n${p.userPrompt}`;
        } else {
          prompt = p.userPrompt;
        }
      }

      // 如果是正在运行的任务，尝试从 hash 获取 analysisReport
      if (!analysisReport && monitorData?.analysisReportHash) {
        const r = await klineDataService.getAnalysisReport(monitorData.analysisReportHash);
        analysisReport = r.analysisReport;
      }

      // 如果是正在运行的任务，从 signals 重构 monitorRules
      if (!monitorRules && monitorData?.signals) {
        const buySignals = monitorData.signals.filter((s: any) => s.type === ORDER_SIDE.BUY);
        const sellSignals = monitorData.signals.filter((s: any) => s.type === ORDER_SIDE.SELL);
        monitorRules = { buySignals, sellSignals };
      }

      setEditPrompt(prompt || '');
      setEditData(
        JSON.stringify(
          {
            analysisReport,
            monitorRules,
          },
          null,
          2,
        ),
      );
    } catch (error) {
      console.error('Failed to load edit data:', error);
      showToast('加载部分数据失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // 验证JSON格式
      let parsedData: any;
      try {
        parsedData = JSON.parse(editData);
      } catch (error) {
        showToast('JSON格式错误，请检查', 'error');
        return;
      }

      // 验证必需字段
      if (!parsedData.monitorRules) {
        showToast('monitorRules 字段不能为空', 'error');
        return;
      }

      setSaving(true);
      await klineDataService.updateAIResponse(symbol, bar, {
        prompt: editPrompt || undefined,
        analysisReport: parsedData.analysisReport || '',
        monitorRules: parsedData.monitorRules,
      });

      showToast('保存成功，监控规则已更新', 'success');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      console.error('Failed to save edit:', error);
      showToast(error.message || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-gray-900 border border-white/20 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">编辑AI响应数据</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/60">
            <svg className="animate-spin h-8 w-8 mr-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            正在加载数据...
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm text-white/80 mb-2">完整提示词：</label>
              <textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                className="w-full h-48 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-cyan-500/50 resize-none mb-4"
                placeholder="提示词内容（根据---分隔符拆分系统提示词和用户提示词）..."
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm text-white/80 mb-2">JSON数据：</label>
              <textarea
                value={editData}
                onChange={(e) => setEditData(e.target.value)}
                className="w-full h-96 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-xs font-mono focus:outline-none focus:border-cyan-500/50 resize-none"
                placeholder='{"analysisReport": "", "monitorRules": {}}'
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white/10 text-white/80 border border-white/20 rounded-lg hover:bg-white/20 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 rounded-lg hover:bg-cyan-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>保存中...</span>
                  </>
                ) : (
                  <span>保存</span>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditMonitorRuleDialog;
